import React, { useCallback, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { FaPlay } from "react-icons/fa";
import { Tracker } from "@/models/tracker";
import { getTrackerValue, getTrackerSource } from "@/utils/tracker-helpers";
import { SourceIcon } from "@/components/resource-card";
import { createTrackerDragPayload } from "@/utils/drag-types";
import { usePanelNavigation } from "@/hooks/use-panel-route";
import SidebarItem, { SidebarItemAction } from "./sidebar-item";
import ResourceContextMenu from "./resource-context-menu";

interface TrackerItemProps {
  tracker: Tracker;
  onRunTracker?: (tracker: Tracker) => void;
  // Multi-select props
  /** Whether this item is selected */
  isSelected?: boolean;
  /** Ordered list of all tracker IDs for range selection */
  orderedIds?: string[];
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
  // Drag-to-group props
  /** Callback when another tracker is dropped on this one to create a group */
  onDropToCreateGroup?: (
    draggedTrackerId: string,
    targetTrackerId: string,
  ) => void;
}

/**
 * TrackerItem component - displays a tracker in the sidebar.
 * Shows the tracker's main value, source icon, and status indicator.
 * Supports drag-and-drop to create groups by dropping one tracker on another.
 */
const TrackerItem: React.FC<TrackerItemProps> = ({
  tracker,
  onRunTracker,
  isSelected,
  orderedIds = [],
  onMultiSelectContextMenu,
  onDropToCreateGroup,
}) => {
  // Panel navigation hook for split-view compatible navigation
  const { navigateTo } = usePanelNavigation();

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const trackerValue = getTrackerValue(tracker);
  const source = getTrackerSource(tracker);
  const isImport = tracker.is_import;

  // Display name: use tracker value or fallback to ID
  const displayName = trackerValue?.value || tracker.id;

  // Create drag payload for context menu
  const dragPayload = createTrackerDragPayload(tracker.id, displayName);

  const handleClick = useCallback(() => {
    navigateTo(`/trackers?id=${tracker.id}`, { title: displayName });
  }, [tracker.id, displayName, navigateTo]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleRun = useCallback(() => {
    onRunTracker?.(tracker);
  }, [tracker, onRunTracker]);

  // Build hover actions
  const hoverActions =
    !isImport && onRunTracker ? (
      <SidebarItemAction
        onClick={handleRun}
        title="Exécuter"
        icon={<FaPlay size={10} />}
        variant="success"
      />
    ) : null;

  return (
    <>
      <SidebarItem
        id={tracker.id}
        name={displayName}
        resourceType="tracker"
        icon={<SourceIcon source={source} size="sm" />}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        isSelected={isSelected}
        orderedIds={orderedIds}
        onMultiSelectContextMenu={onMultiSelectContextMenu}
        enableDrag={true}
        onDropToCreateGroup={onDropToCreateGroup}
        dragPayload={dragPayload}
        hoverActions={hoverActions}
      />

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <ResourceContextMenu
            position={contextMenu}
            dragPayload={dragPayload}
            onClose={closeContextMenu}
          />
        )}
      </AnimatePresence>
    </>
  );
};

TrackerItem.displayName = "TrackerItem";

export default TrackerItem;
